import { BreakpointsEnum, CommonComponentTypes } from '@app/types';
import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { COLORS, TYPOGRAPHY } from '@app/constants';

import { BannerCircles } from '@app/ui/iconComponents';

type BubblesOffset = Partial<
  Record<
    'mobile' | 'desktop',
    {
      x: number;
      y: number;
    }
  >
>;

interface BannerBaseProps extends CommonComponentTypes {
  /**
   * The main banner text
   */
  content: ReactNode;
  /**
   * Width of the main text
   */
  maxTitleWidth?: string;
  /**
   * Background color
   */
  color: string;
  /**
   * Background text at the top
   */
  backgroundText?: string;
  /**
   * Width of the background text
   */
  maxBackgroundTextWidthMobile?: string;
  /**
   * Bubbles offset, in pixels
   */
  bubblesOffset?: BubblesOffset;
  /**
   * Apply margin at the bottom
   */
  margin?: boolean;
  /**
   * Image decorators
   */
  children?: ReactNode;
}

/**
 * Props for banners
 */
export interface CommonBannerProps extends CommonComponentTypes {
  title?: ReactNode;
  margin?: boolean;
}

const BannerBase: FC<BannerBaseProps> = ({
  className,
  content,
  maxTitleWidth,
  maxBackgroundTextWidthMobile,
  children,
  color,
  backgroundText,
  bubblesOffset,
  margin,
}) => {
  return (
    <Root className={className} $color={color} $margin={margin}>
      <BackgroundDecorations $bubblesOffset={bubblesOffset}>
        <BackgoundText $maxWidthMobile={maxBackgroundTextWidthMobile}>
          {backgroundText}
        </BackgoundText>
        <BannerCircles />
      </BackgroundDecorations>
      <Content $maxWidth={maxTitleWidth}>{content}</Content>
      <ImageDecorations>{children}</ImageDecorations>
    </Root>
  );
};

export default BannerBase;

const Root = styled.div<{ $color: string; $margin?: boolean }>`
  overflow: hidden;
  position: relative;
  z-index: 2;
  border-radius: 12px;
  background-color: ${(props) => props.$color};
  min-height: 176px;
  padding: 46px 36px;
  display: flex;
  align-items: center;

  ${(props) =>
    props.$margin &&
    css`
      margin-bottom: 24px;

      @media (max-width: ${BreakpointsEnum.sm}px) {
        margin-bottom: 44px;
      }
    `}

  @media (max-width: ${BreakpointsEnum.sm}px) {
    border-radius: 20px;
    min-height: 133px;
    padding: 25px 24px;
  }
`;

const BackgroundDecorations = styled.div<{ $bubblesOffset?: BubblesOffset }>`
  position: absolute;
  top: -22px;
  left: 19px;
  display: flex;
  gap: 36px;
  justify-content: space-between;
  width: 100%;

  svg {
    flex-shrink: 0;

    ${(props) =>
      props.$bubblesOffset?.desktop
        ? css`
            transform: translate(
              ${props.$bubblesOffset.desktop.x}px,
              ${props.$bubblesOffset.desktop.y}px
            );
          `
        : css`
            transform: translateY(-60px);
          `}
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    left: 11px;
    top: -17px;
    gap: 25px;

    svg {
      width: 170px;
      height: 162px;

      ${(props) =>
        props.$bubblesOffset?.mobile
          ? css`
              transform: translate(
                ${props.$bubblesOffset.mobile.x}px,
                ${props.$bubblesOffset.mobile.y}px
              );
            `
          : css`
              transform: translateY(-60px);
            `}
    }
  }
`;

const BackgoundText = styled.p<{ $maxWidthMobile?: string }>`
  font-weight: 800;
  font-size: 96px;
  opacity: 0.15;
  color: #fff;
  flex-shrink: 0;

  ${(props) =>
    props.$maxWidthMobile
      ? css`
          max-width: ${props.$maxWidthMobile};
        `
      : css`
          min-width: 418px;
        `}

  @media (max-width: ${BreakpointsEnum.sm}px) {
    min-width: 230px;
    font-size: 64px;
  }
`;

const Content = styled.p<{ $maxWidth?: string }>`
  position: relative;
  z-index: 2;
  color: ${COLORS.grayscale100};
  width: 60%;

  ${(props) =>
    props.$maxWidth
      ? css`
          max-width: ${props.$maxWidth};
        `
      : css`
          max-width: 478px;
        `}

  ${TYPOGRAPHY.bodyB7medium12};

  b {
    ${TYPOGRAPHY.bodyB7bold12};
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    width: 70%;
    max-width: 350px;

    ${TYPOGRAPHY.bodyB12medium14};

    b {
      ${TYPOGRAPHY.bodyB12bold14};
    }
  }
`;

const ImageDecorations = styled.div`
  position: absolute;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  height: 100%;
  right: 0;
`;
